import { useCallback, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { SideNav, TopHeaderMenuButton } from '@carboledger/ui';
import { MODULE_LABELS, ROUTES } from '@/app/routes';
import { useTranslations, useCurrentUser } from '@/hooks';
import { buildWorkspaceNav, findActiveNavId } from '@/config/navigation';
import {
  buildFacilityNodes,
  selectedFacilityValue,
  facilityLabel,
  openPathFor,
} from '@/config/workspaceFacilities';
import { useSelectFacilityMutation } from '@/lib/api/accountApi';
import { ChatbotWidget } from '@/slices/chatbot';
import { AppTopHeader } from './AppTopHeader';
import { FacilitySelector } from './FacilitySelector';
import styles from './WorkspaceShell.module.scss';

/**
 * Props:
 *   buildNav  (module, t) => nav items; defaults to buildWorkspaceNav
 */
export const WorkspaceShell = ({ buildNav = buildWorkspaceNav }) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { module } = useParams();
  const user = useCurrentUser();
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [selectFacility, { isLoading: isSwitching }] = useSelectFacilityMutation();

  const nav = useMemo(() => buildNav(module, t), [buildNav, module, t]);
  const activeNavId = useMemo(() => findActiveNavId(pathname, nav), [pathname, nav]);

  const handleCollapseToggle = useCallback(() => setNavCollapsed((collapsed) => !collapsed), []);
  const handleNavigate = useCallback(
    (node) => {
      if (node.link) navigate(node.link);
    },
    [navigate],
  );
  const handleScheme = useCallback(() => {
    // TODO: open the module (ISCC EU/PLUS/CORSIA) switcher.
  }, []);

  const schemeSelector = useMemo(
    () => <TopHeaderMenuButton label={MODULE_LABELS[module] ?? module} onClick={handleScheme} />,
    [module, handleScheme],
  );

  const facilityNodes = useMemo(
    () =>
      buildFacilityNodes(t, {
        sites: user?.sites,
        storages: user?.storages,
        backToBackAccounts: user?.backToBackAccounts,
      }),
    [t, user],
  );
  const facilityValue = useMemo(() => selectedFacilityValue(user?.selection), [user]);

  // The save re-reads the session, so the label follows from the server, never local state.
  const handleSelectFacility = useCallback(
    (value) => {
      selectFacility(value);
    },
    [selectFacility],
  );

  const facilityExpandLabel = useCallback((name) => t('WORKSPACE_SHOW_TERMINALS', { name }), [t]);

  const facilitySelector = useMemo(
    () => (
      <FacilitySelector
        label={facilityLabel(facilityValue, facilityNodes, t('WORKSPACE_ALL_FACILITIES'))}
        nodes={facilityNodes}
        value={facilityValue}
        defaultOpenIds={openPathFor(facilityValue)}
        expandLabel={facilityExpandLabel}
        disabled={isSwitching}
        onSelect={handleSelectFacility}
      />
    ),
    [t, facilityValue, facilityNodes, facilityExpandLabel, isSwitching, handleSelectFacility],
  );

  if (!MODULE_LABELS[module]) return <Navigate to={ROUTES.home} replace />;

  return (
    <div className={styles.page}>
      <AppTopHeader menu={schemeSelector} actions={facilitySelector} />

      <div className={styles.body}>
        <SideNav
          items={nav}
          activeId={activeNavId}
          collapsed={navCollapsed}
          onCollapseToggle={handleCollapseToggle}
          onNavigate={handleNavigate}
        />
        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
      <ChatbotWidget module={module} />
    </div>
  );
};
